'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode, useRef, useCallback } from 'react';
import calendarService, { 
  DayData, 
  BlockedPeriod, 
  CalendarBooking, 
  CalendarSettings,
  PricingRule
} from '../services/calendarService';
import { toast } from 'react-hot-toast';

// Constants for timeout durations
const FETCH_TIMEOUT_MS = 5000;
const OPERATION_TIMEOUT_MS = 3000;

// Define event types for calendar changes
export type CalendarEventType = 
  | 'day-updated'
  | 'settings-updated'
  | 'booking-updated'
  | 'blocked-period-added'
  | 'blocked-period-removed'
  | 'pricing-rule-added'
  | 'pricing-rule-removed';

// Define event listener type
export type CalendarEventListener = (eventType: CalendarEventType, data?: any) => void;

// Define loading state type with more granularity
interface LoadingState {
  calendar: boolean;
  settings: boolean;
  bookings: boolean;
  operations: boolean;
}

// Define error state type
interface ErrorState {
  calendar: Error | null;
  settings: Error | null;
  bookings: Error | null;
  operations: Error | null;
}

// Define context type
interface CalendarContextType {
  // Data
  calendarData: Record<string, DayData>;
  settings: CalendarSettings | null;
  bookings: CalendarBooking[];
  
  // Loading states
  isLoading: boolean; // Aggregate loading state (any loading = true)
  loadingState: LoadingState; // Granular loading states
  errors: ErrorState; // Error states
  
  // Methods
  fetchCalendarData: (year: number, month: number) => Promise<Record<string, DayData>>;
  fetchCalendarSettings: () => Promise<CalendarSettings>;
  updateDayData: (dayData: DayData) => Promise<DayData>;
  updateCalendarSettings: (settings: CalendarSettings) => Promise<CalendarSettings>;
  addBlockedPeriod: (period: Omit<BlockedPeriod, 'id'>) => Promise<BlockedPeriod | null>;
  deleteBlockedPeriod: (id: string) => Promise<boolean>;
  addPricingRule: (rule: Omit<PricingRule, 'id'>) => Promise<void>;
  deletePricingRule: (id: string) => Promise<void>;
  updateBookingStatus: (id: string, status: CalendarBooking['status']) => Promise<void>;
  
  // Loading state management
  setIsLoading: (key: keyof LoadingState, value: boolean) => void;
  resetLoadingState: () => void;
  
  // Error management
  clearErrors: () => void;
  
  // Event listeners
  addEventListener: (listener: CalendarEventListener) => void;
  removeEventListener: (listener: CalendarEventListener) => void;
  
  // Cache management
  clearCache: () => void;
}

// Create context
const CalendarContext = createContext<CalendarContextType | undefined>(undefined);

// Provider component
export function CalendarProvider({ children }: { children: ReactNode }) {
  // Data state
  const [calendarData, setCalendarData] = useState<Record<string, DayData>>({});
  const [settings, setSettings] = useState<CalendarSettings | null>(null);
  const [bookings, setBookings] = useState<CalendarBooking[]>([]);
  
  // Loading state with more granularity
  const [loadingState, setLoadingState] = useState<LoadingState>({
    calendar: true,
    settings: true,
    bookings: true,
    operations: false
  });
  
  // Aggregate loading state
  const [isLoading, setIsLoading] = useState<boolean>(true);
  
  // Error state
  const [errors, setErrors] = useState<ErrorState>({
    calendar: null,
    settings: null,
    bookings: null,
    operations: null
  });
  
  // Event listeners
  const [eventListeners, setEventListeners] = useState<CalendarEventListener[]>([]);
  
  // Cache for calendar data to prevent redundant fetches
  const calendarCache = useRef<Record<string, Record<string, DayData>>>({});
  
  // Timeout references for cleanup
  const timeoutRefs = useRef<Record<string, NodeJS.Timeout>>({});
  
  // Helper to set loading state for a specific key
  const setLoadingStateForKey = useCallback((key: keyof LoadingState, value: boolean) => {
    setLoadingState(prev => {
      const newState = { ...prev, [key]: value };
      return newState;
    });
    
    // Update aggregate loading state
    setIsLoading(prev => {
      if (value) return true; // If setting any to true, overall is true
      // If setting to false, check if any others are still true
      const newState = { ...loadingState, [key]: false };
      return Object.values(newState).some(state => state === true);
    });
  }, [loadingState]);
  
  // Reset all loading states
  const resetLoadingState = useCallback(() => {
    setLoadingState({
      calendar: false,
      settings: false,
      bookings: false,
      operations: false
    });
    setIsLoading(false);
    
    // Clear all timeouts
    Object.values(timeoutRefs.current).forEach(timeout => clearTimeout(timeout));
    timeoutRefs.current = {};
  }, []);
  
  // Clear cache
  const clearCache = useCallback(() => {
    calendarCache.current = {};
    console.log('CalendarContext: Cache cleared');
  }, []);
  
  // Fetch calendar settings
  const fetchCalendarSettings = useCallback(async (): Promise<CalendarSettings> => {
    try {
      setLoadingStateForKey('settings', true);
      
      // Set safety timeout
      const timeoutId = setTimeout(() => {
        console.log('CalendarContext: Settings fetch safety timeout triggered');
        setLoadingStateForKey('settings', false);
        setErrors(prev => ({
          ...prev,
          settings: new Error('Settings fetch timed out')
        }));
      }, FETCH_TIMEOUT_MS);
      
      // Store timeout reference for cleanup
      timeoutRefs.current['settings'] = timeoutId;
      
      // Fetch settings from service
      const fetchedSettings = await calendarService.getCalendarSettings();
      
      // Clear timeout
      clearTimeout(timeoutId);
      delete timeoutRefs.current['settings'];
      
      // Update state
      setSettings(fetchedSettings);
      setLoadingStateForKey('settings', false);
      
      return fetchedSettings;
    } catch (error) {
      console.error('Error fetching calendar settings:', error);
      setLoadingStateForKey('settings', false);
      setErrors(prev => ({
        ...prev,
        settings: error instanceof Error ? error : new Error('Failed to fetch calendar settings')
      }));
      throw error;
    }
  }, [setLoadingStateForKey]);
  
  // Clear errors
  const clearErrors = useCallback(() => {
    setErrors({
      calendar: null,
      settings: null,
      bookings: null,
      operations: null
    });
  }, []);
  
  // Initialize data with proper error handling and timeouts
  useEffect(() => {
    const initializeData = async () => {
      // Set safety timeout for the entire initialization process
      timeoutRefs.current.init = setTimeout(() => {
        console.log('Calendar initialization timed out');
        resetLoadingState();
        toast.error('Calendar initialization timed out. Please refresh the page.');
      }, FETCH_TIMEOUT_MS);
      
      try {
        console.log('Initializing calendar data...');
        
        // Fetch initial settings with timeout
        setLoadingStateForKey('settings', true);
        try {
          const settingsPromise = calendarService.getCalendarSettings();
          const settingsWithTimeout = Promise.race([
            settingsPromise,
            new Promise<never>((_, reject) => {
              timeoutRefs.current.settings = setTimeout(() => {
                reject(new Error('Settings fetch timeout'));
              }, FETCH_TIMEOUT_MS);
            })
          ]);
          
          const calendarSettings = await settingsWithTimeout;
          console.log('Calendar settings loaded:', calendarSettings);
          setSettings(calendarSettings);
          setErrors(prev => ({ ...prev, settings: null }));
        } catch (error) {
          console.error('Error loading calendar settings:', error);
          setErrors(prev => ({ ...prev, settings: error as Error }));
          toast.error('Failed to load calendar settings');
        } finally {
          setLoadingStateForKey('settings', false);
          if (timeoutRefs.current.settings) {
            clearTimeout(timeoutRefs.current.settings);
          }
        }
        
        // Fetch initial bookings with timeout
        setLoadingStateForKey('bookings', true);
        try {
          const bookingsPromise = calendarService.getBookings();
          const bookingsWithTimeout = Promise.race([
            bookingsPromise,
            new Promise<never>((_, reject) => {
              timeoutRefs.current.bookings = setTimeout(() => {
                reject(new Error('Bookings fetch timeout'));
              }, FETCH_TIMEOUT_MS);
            })
          ]);
          
          const bookingsData = await bookingsWithTimeout;
          console.log('Bookings loaded:', bookingsData.length);
          setBookings(bookingsData);
          setErrors(prev => ({ ...prev, bookings: null }));
        } catch (error) {
          console.error('Error loading bookings:', error);
          setErrors(prev => ({ ...prev, bookings: error as Error }));
          toast.error('Failed to load bookings');
        } finally {
          setLoadingStateForKey('bookings', false);
          if (timeoutRefs.current.bookings) {
            clearTimeout(timeoutRefs.current.bookings);
          }
        }
        
        // Fetch current month's data with timeout
        setLoadingStateForKey('calendar', true);
        try {
          const currentDate = new Date();
          const year = currentDate.getFullYear();
          const month = currentDate.getMonth();
          console.log('Fetching calendar data for current month:', year, month);
          
          // Check cache first
          const cacheKey = `${year}-${month}`;
          if (calendarCache.current[cacheKey]) {
            console.log('Using cached calendar data for', cacheKey);
            setCalendarData(calendarCache.current[cacheKey]);
          } else {
            // Fetch with timeout
            const calendarPromise = calendarService.getCalendarData(year, month);
            const daysDataWithTimeout = Promise.race([
              calendarPromise,
              new Promise<never>((_, reject) => {
                timeoutRefs.current.calendar = setTimeout(() => {
                  reject(new Error('Calendar data fetch timeout'));
                }, FETCH_TIMEOUT_MS);
              })
            ]);
            
            const daysData = await daysDataWithTimeout;
            console.log('Calendar data loaded:', daysData.length, 'days');
            
            // Convert array to record with date as key
            const dataRecord: Record<string, DayData> = {};
            daysData.forEach(day => {
              dataRecord[day.date] = day;
            });
            
            // Update state and cache
            setCalendarData(dataRecord);
            calendarCache.current[cacheKey] = dataRecord;
          }
          
          setErrors(prev => ({ ...prev, calendar: null }));
        } catch (error) {
          console.error('Error fetching calendar data:', error);
          setErrors(prev => ({ ...prev, calendar: error as Error }));
          toast.error('Failed to load calendar data');
        } finally {
          setLoadingStateForKey('calendar', false);
          if (timeoutRefs.current.calendar) {
            clearTimeout(timeoutRefs.current.calendar);
          }
        }
      } catch (error) {
        console.error('Error initializing calendar data:', error);
        toast.error('Failed to initialize calendar data');
      } finally {
        // Clear the main initialization timeout
        if (timeoutRefs.current.init) {
          clearTimeout(timeoutRefs.current.init);
        }
      }
    };
    const handleLoadingTimeout = () => {
      console.log('CalendarContext: Received loading timeout event, forcing loading state to false');
      setIsLoading(false);
      toast.error('Calendar loading timed out. Please try again.');
    };
    
    window.addEventListener('calendar-loading-timeout', handleLoadingTimeout);
    
    initializeData();
    
    return () => {
      window.removeEventListener('calendar-loading-timeout', handleLoadingTimeout);
    };
  }, []);
  
  // Notify listeners of events
  const notifyListeners = (eventType: CalendarEventType, data?: any) => {
    eventListeners.forEach(listener => listener(eventType, data));
  };
  
  // Fetch calendar data for a specific month
  const fetchCalendarData = async (year: number, month: number): Promise<Record<string, DayData>> => {
    console.log(`CalendarContext: fetchCalendarData called for ${year}-${month+1}`);
    try {
      // Set loading state
      console.log('CalendarContext: Setting isLoading to true');
      setIsLoading(true);
      
      // Fetch data from service with a timeout to prevent hanging
      console.log('CalendarContext: Calling calendarService.getCalendarData');
      
      // Create a promise that resolves after a timeout
      const timeoutPromise = new Promise<DayData[]>((_, reject) => {
        setTimeout(() => {
          reject(new Error('Calendar data fetch timed out'));
        }, 5000); // 5 second timeout
      });
      
      // Race between the actual data fetch and the timeout
      const daysData = await Promise.race([
        calendarService.getCalendarData(year, month),
        timeoutPromise
      ]);
      
      console.log(`CalendarContext: Received ${daysData.length} days from service`);
      
      // Convert array to record with date as key
      const dataRecord: Record<string, DayData> = {};
      daysData.forEach(day => {
        dataRecord[day.date] = day;
      });
      console.log(`CalendarContext: Processed ${Object.keys(dataRecord).length} days`);
      
      // Update state
      console.log('CalendarContext: Updating calendarData state');
      setCalendarData(prevData => {
        const newData = {
          ...prevData,
          ...dataRecord
        };
        console.log(`CalendarContext: Calendar data updated, now has ${Object.keys(newData).length} days`);
        return newData;
      });
      
      // Notify listeners of the update
      notifyListeners('day-updated');
      
      // Make sure to set loading to false before returning
      console.log('CalendarContext: Setting isLoading to false');
      setIsLoading(false);
      return dataRecord;
    } catch (error) {
      console.error('CalendarContext: Error fetching calendar data:', error);
      toast.error('Failed to load calendar data');
      // Make sure to set loading to false even when there's an error
      console.log('CalendarContext: Setting isLoading to false after error');
      setIsLoading(false);
      
      // Try to recover by returning any existing data we might have
      const existingDates = Object.values(calendarData)
        .filter(day => {
          const date = new Date(day.date);
          return date.getFullYear() === year && date.getMonth() === month;
        })
        .reduce((acc, day) => {
          acc[day.date] = day;
          return acc;
        }, {} as Record<string, DayData>);
      
      if (Object.keys(existingDates).length > 0) {
        console.log(`CalendarContext: Recovered ${Object.keys(existingDates).length} days from existing data`);
        return existingDates;
      }
      
      // Return empty object as last resort
      return {};
    }
  };
  
  // Update a specific day's data
  const updateDayData = async (dayData: DayData) => {
    try {
      setIsLoading(true);
      
      // Update via service
      const updatedDay = await calendarService.updateDayData(dayData);
      
      // Update local state
      setCalendarData(prevData => ({
        ...prevData,
        [updatedDay.date]: updatedDay
      }));
      
      // Notify listeners
      notifyListeners('day-updated', updatedDay);
      setIsLoading(false);
      return updatedDay;
    } catch (error) {
      console.error('Error updating day data:', error);
      toast.error('Failed to update day data');
      setIsLoading(false);
      // Return the original data instead of throwing error
      return dayData;
    }
  };
  
  // Update calendar settings
  const updateCalendarSettings = async (newSettings: CalendarSettings) => {
    try {
      setIsLoading(true);
      
      // Update via service
      const updatedSettings = await calendarService.updateCalendarSettings(newSettings);
      
      // Update local state
      setSettings(updatedSettings);
      
      // Notify listeners
      notifyListeners('settings-updated', updatedSettings);
      setIsLoading(false);
      return updatedSettings;
    } catch (error) {
      console.error('Error updating calendar settings:', error);
      toast.error('Failed to update calendar settings');
      setIsLoading(false);
      // Return the original settings instead of throwing error
      return newSettings;
    }
  };
  
  // Add blocked period
  const addBlockedPeriod = async (period: Omit<BlockedPeriod, 'id'>) => {
    try {
      setIsLoading(true);
      
      // Add via service
      const newPeriod = await calendarService.addBlockedPeriod(period);
      
      // Update local state
      setSettings(prevSettings => {
        if (!prevSettings) return prevSettings;
        
        return {
          ...prevSettings,
          blockedPeriods: [...prevSettings.blockedPeriods, newPeriod]
        };
      });
      
      // Force refresh of calendar data for affected dates
      const startDate = new Date(period.startDate);
      const endDate = new Date(period.endDate);
      const startYear = startDate.getFullYear();
      const startMonth = startDate.getMonth();
      const endYear = endDate.getFullYear();
      const endMonth = endDate.getMonth();
      
      // Clear cache for affected months
      if (calendarCache.current) {
        for (let year = startYear; year <= endYear; year++) {
          const monthStart = year === startYear ? startMonth : 0;
          const monthEnd = year === endYear ? endMonth : 11;
          
          for (let month = monthStart; month <= monthEnd; month++) {
            const cacheKey = `${year}-${month}`;
            if (calendarCache.current[cacheKey]) {
              console.log(`CalendarContext: Clearing cache for ${cacheKey} due to blocked period change`);
              delete calendarCache.current[cacheKey];
            }
          }
        }
      }
      
      // Notify listeners
      notifyListeners('blocked-period-added', newPeriod);
      
      // Also notify day-updated for all affected days
      const currentDate = new Date(startDate);
      while (currentDate <= endDate) {
        const dateString = currentDate.toISOString().split('T')[0];
        notifyListeners('day-updated', { date: dateString, available: false });
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      setIsLoading(false);
      return newPeriod;
    } catch (error) {
      console.error('Error adding blocked period:', error);
      toast.error('Failed to add blocked period');
      setIsLoading(false);
      // Return null instead of throwing error
      return null;
    }
  };
  
  // Add pricing rule
  const addPricingRule = async (rule: Omit<PricingRule, 'id'>) => {
    try {
      setIsLoading(true);
      
      // Add via service
      const newRule = await calendarService.addPricingRule(rule);
      
      // Update local state
      setSettings(prevSettings => {
        if (!prevSettings) return prevSettings;
        
        return {
          ...prevSettings,
          seasonalRules: [...prevSettings.seasonalRules, newRule]
        };
      });
      
      // Notify listeners
      notifyListeners('pricing-rule-added', newRule);
      
      // Also notify day-updated for all affected days
      const startDate = new Date(rule.startDate);
      const endDate = new Date(rule.endDate);
      const currentDate = new Date(startDate);
      
      while (currentDate <= endDate) {
        const dateString = currentDate.toISOString().split('T')[0];
        notifyListeners('day-updated', { date: dateString });
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      setIsLoading(false);
    } catch (error) {
      console.error('Error adding pricing rule:', error);
      toast.error('Failed to add pricing rule');
      setIsLoading(false);
      throw error;
    }
  };
  
  // Delete blocked period
  const deleteBlockedPeriod = async (id: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      
      // Delete via service
      await calendarService.deleteBlockedPeriod(id);
      
      // Update local state
      setSettings(prevSettings => {
        if (!prevSettings) return prevSettings;
        
        return {
          ...prevSettings,
          blockedPeriods: prevSettings.blockedPeriods.filter(period => period.id !== id)
        };
      });
      
      // Notify listeners
      notifyListeners('blocked-period-removed', { id });
      setIsLoading(false);
      return true;
    } catch (error) {
      console.error('Error deleting blocked period:', error);
      toast.error('Failed to delete blocked period');
      setIsLoading(false);
      return false;
    }
  };
  
  // Delete pricing rule
  const deletePricingRule = async (id: string): Promise<void> => {
    try {
      setIsLoading(true);
      
      // Delete via service
      await calendarService.deletePricingRule(id);
      
      // Update local state
      setSettings(prevSettings => {
        if (!prevSettings) return prevSettings;
        
        return {
          ...prevSettings,
          seasonalRules: prevSettings.seasonalRules.filter(rule => rule.id !== id)
        };
      });
      
      // Notify listeners
      notifyListeners('pricing-rule-removed', { id });
      setIsLoading(false);
    } catch (error) {
      console.error('Error deleting pricing rule:', error);
      toast.error('Failed to delete pricing rule');
      setIsLoading(false);
    }
  };
  
  // Update booking status
  const updateBookingStatus = async (id: string, status: CalendarBooking['status']) => {
    try {
      setIsLoading(true);
      
      // Update via service
      const updatedBooking = await calendarService.updateBookingStatus(id, status);
      
      // Update local state
      setBookings(prevBookings => 
        prevBookings.map(booking => 
          booking.id === id ? updatedBooking : booking
        )
      );
      
      // Notify listeners
      notifyListeners('booking-updated', updatedBooking);
    } catch (error) {
      console.error('Error updating booking status:', error);
      toast.error('Failed to update booking status');
      throw error;
    }
  };
  
  // Add event listener
  const addEventListener = (listener: CalendarEventListener) => {
    setEventListeners(prev => [...prev, listener]);
  };
  
  // Remove event listener
  const removeEventListener = (listener: CalendarEventListener) => {
    setEventListeners(prev => prev.filter(l => l !== listener));
  };
  
  // Context value
  const contextValue: CalendarContextType = {
    calendarData,
    settings,
    bookings,
    isLoading,
    loadingState,
    errors,
    fetchCalendarData,
    fetchCalendarSettings,
    updateDayData,
    updateCalendarSettings,
    addBlockedPeriod,
    deleteBlockedPeriod,
    addPricingRule,
    deletePricingRule,
    updateBookingStatus,
    setIsLoading: setLoadingStateForKey,
    resetLoadingState,
    clearErrors,
    addEventListener,
    removeEventListener,
    clearCache
  };
  
  return (
    <CalendarContext.Provider value={contextValue}>
      {children}
    </CalendarContext.Provider>
  );
}

// Custom hook to use calendar context
export function useCalendar() {
  const context = useContext(CalendarContext);
  
  if (context === undefined) {
    throw new Error('useCalendar must be used within a CalendarProvider');
  }
  
  return context;
}

// Custom hook for real-time calendar updates
export function useCalendarSync(onUpdate?: (eventType: CalendarEventType, data?: any) => void) {
  const calendar = useCalendar();
  
  useEffect(() => {
    // Skip if no callback provided
    if (!onUpdate) return;
    
    // Create listener function
    const listener = (eventType: CalendarEventType, data?: any) => {
      onUpdate(eventType, data);
    };
    
    // Add listener
    calendar.addEventListener(listener);
    
    // Clean up
    return () => {
      calendar.removeEventListener(listener);
    };
  }, [calendar, onUpdate]);
  
  return calendar;
}
